import { isEmpty } from 'lodash'
import AccountApi from '@/api/account'
import router from '@/router'

const SET_PLAN_MUTATION = 'setPlan'

const _state = {
  salesAssistedContract: null,
}

const actions = {
  getSalesAssistedContractData: async ({ commit, rootState }) => {
    const { plan, currentUser } = rootState
    const isFreePlan = plan?.group === 'free'
    const isInTeam = currentUser?.accountId
    // check if user is free plan or not in team
    if (isFreePlan || !isInTeam) return
    const salesAssistedContract = await AccountApi.getSalesAssistedContract()
    commit('setSalesAssistedContract', salesAssistedContract)
  },
  // A/B test action for upgrade button click
  handleUpgradeButtonClick: async ({ dispatch, rootGetters }, payload) => {
    const canUpgradeButtonGoToStripe = rootGetters['featureToggles/canUpgradeButtonGoToStripe']

    if (canUpgradeButtonGoToStripe) {
      // Variants 1 & 2: Call Stripe upgrade action
      await dispatch('upgradePlanAction', payload, { root: true })
    } else {
      // Variant 3: Navigate to user-plan page
      router.push('/apps/user-plan?redirect=/apps/user-plan')
    }
  },
}

const UPGRADE_FLAG_NAME = 'AHA-37476-enable-upgrade-button-link-stripe'
const VARIANT_BUTTON_WITH_LINK = 'button-with-link'

const FEATURE_TOGGLES_IS_ENABLED = 'featureToggles/isFeatureEnabled'
const FEATURE_TOGGLES_GET_VARIANT = 'featureToggles/getVariant'
const MAXED_OUT_PLAN_GROUPS = ['pro', 'white-label']
const MAXED_OUT_PLAN_TYPES = [
  'pro-monthly',
  'pro-yearly',
  'white-label',
  'edu-large',
  'edu-large-yearly',
  'edu-large-monthly',
]
const UPGRADE_ALLOWED_TEAM_ROLES = ['owner', 'admin']

const isTeamAccountWithoutUpgradePermission = (currentUser) => {
  return currentUser.accountId && !UPGRADE_ALLOWED_TEAM_ROLES.includes(currentUser.accountRole)
}

const isMaxedOutProfileMenuUpgradePlan = (plan, planType) => {
  const planName = (plan.name || '').toLowerCase()
  return MAXED_OUT_PLAN_GROUPS.includes(plan.group) ||
    MAXED_OUT_PLAN_TYPES.includes(plan.type) ||
    MAXED_OUT_PLAN_TYPES.includes(planType) ||
    planName === 'enterprise' ||
    planName.includes('pro') ||
    (planName.includes('edu') && planName.includes('large'))
}

const _getters = {
  canUseNewBadgeComponent: (_, __, ___, rootGetters) => {
    return rootGetters[FEATURE_TOGGLES_IS_ENABLED]('release-new-plan-badge-component')
  },
  isEnterprise: (state, getters) => {
    if (!getters.canUseNewBadgeComponent) return false
    return !isEmpty(state.salesAssistedContract)
  },
  canUseOneTime2025: (_, __, ___, rootGetters) => {
    return rootGetters[FEATURE_TOGGLES_IS_ENABLED]('release-one-time-2025')
  },
  canUseNewFreePlan: (_, __, ___, rootGetters) => {
    const variant = rootGetters[FEATURE_TOGGLES_GET_VARIANT]('release-AHA-41379-new-free-plan')
    return variant?.name === 'treatment'
  },
  canShowProfileMenuUpgrade: (state, getters, rootState, rootGetters) => {
    if (!rootGetters['featureToggles/canUseSamePricingDisplay']) return false

    const plan = rootState.plan || {}
    const currentUser = rootState.currentUser || {}
    const planType = currentUser.plan || plan.type || ''

    if (!planType && !plan.name) return false
    if (getters.isEnterprise || !isEmpty(state.salesAssistedContract)) return false
    if (isTeamAccountWithoutUpgradePermission(currentUser)) return false
    if (isMaxedOutProfileMenuUpgradePlan(plan, planType)) return false

    return true
  },
  // A/B test getters for upgrade button flow
  upgradeButtonVariant: (_, __, ___, rootGetters) => {
    return rootGetters['featureToggles/getVariant'](UPGRADE_FLAG_NAME)
  },
  shouldShowSeeAllPlansLink: (_, getters) => {
    // Only show the link for variant 2 (button-with-link)
    return getters.upgradeButtonVariant?.name === VARIANT_BUTTON_WITH_LINK
  },
  shouldUseStripeUpgrade: (_, __, ___, rootGetters) => {
    // Use Stripe for variants 1 and 2 (when flag is enabled)
    return rootGetters[FEATURE_TOGGLES_IS_ENABLED](UPGRADE_FLAG_NAME)
  },
}

const mutations = {
  setSalesAssistedContract(state, payload) {
    state.salesAssistedContract = payload
  },
}

const plugin = (store) => {
  store.subscribe((mutation) => {
    // WHY SET_PLAN?
    // Because the setPlan is always called after setCurrentUser mutation
    // the flow is currentUser -> plan -> salesAssistedContract data
    if (mutation.type !== SET_PLAN_MUTATION) return
    store.dispatch('planModule/getSalesAssistedContractData')
  })
}

export default {
  namespaced: true,
  state: _state,
  actions,
  mutations,
  getters: _getters,
  plugin,
}
